/**
 * Temas visuales disponibles para el sidebar.
 *
 * Cada tema define el set completo de colores (fondo, texto, hover, activo)
 * para garantizar combinaciones con contraste adecuado y armonia visual.
 * El tenant elige el suyo desde la edicion del tenant (TenantDetail).
 *
 * Si el tenant no define tema, se usa SLATE (default).
 */
export type SidebarThemeId = 'SLATE' | 'SKY' | 'EMERALD' | 'FUCHSIA';

export type SidebarThemeConfig = {
  id: SidebarThemeId;
  label: string;
  /** Color de fondo principal del sidebar. */
  bg: string;
  /** Color de fondo del header del sidebar (mas solido que bg). */
  bgSubtle: string;
  /** Color del borde derecho del sidebar. */
  border: string;
  /** Color de texto para items en estado reposo. */
  textBase: string;
  /** Color de texto para el titulo del modulo. */
  textHeading: string;
  /** Color para texto muted (toggle, labels secundarios). */
  textMuted: string;
  /** Clase hover bg para items. */
  hoverBg: string;
  /** Clase hover text para items. */
  hoverText: string;
  /** Color de fondo del item activo. */
  activeBg: string;
  /** Color de texto del item activo. */
  activeText: string;
  /** Color de texto del boton toggle colapsar. */
  toggleText: string;
  /** Color hover bg del boton toggle colapsar. */
  toggleHover: string;
  /** Color de acento para borde superior de pagina (misma familia que el sidebar). */
  pageAccent: string;
};

export const SIDEBAR_THEMES: Record<SidebarThemeId, SidebarThemeConfig> = {
  SLATE: {
    id: 'SLATE',
    label: 'Claro (default)',
    bg: 'bg-white',
    bgSubtle: 'bg-slate-50',
    border: 'border-slate-200',
    textBase: 'text-slate-700',
    textHeading: 'text-slate-800',
    textMuted: 'text-slate-500',
    hoverBg: 'hover:bg-slate-100',
    hoverText: 'hover:text-slate-900',
    activeBg: 'bg-primary',
    activeText: 'text-white',
    toggleText: 'text-slate-600',
    toggleHover: 'hover:bg-slate-100',
    pageAccent: 'bg-primary',
  },
  SKY: {
    id: 'SKY',
    label: 'Azul',
    bg: 'bg-[#1e40af]',
    bgSubtle: 'bg-[#1e3a8a]',
    border: 'border-white/10',
    textBase: 'text-white/90',
    textHeading: 'text-white/60',
    textMuted: 'text-white/70',
    hoverBg: 'hover:bg-white/10',
    hoverText: 'hover:text-white',
    activeBg: 'bg-white/15',
    activeText: 'text-white',
    toggleText: 'text-white/70',
    toggleHover: 'hover:bg-white/10',
    pageAccent: 'bg-[#1e40af]',
  },
  EMERALD: {
    id: 'EMERALD',
    label: 'Esmeralda',
    bg: 'bg-[#065f46]',
    bgSubtle: 'bg-[#064e3b]',
    border: 'border-white/10',
    textBase: 'text-white/90',
    textHeading: 'text-white/60',
    textMuted: 'text-white/70',
    hoverBg: 'hover:bg-white/10',
    hoverText: 'hover:text-white',
    activeBg: 'bg-white/15',
    activeText: 'text-white',
    toggleText: 'text-white/70',
    toggleHover: 'hover:bg-white/10',
    pageAccent: 'bg-[#065f46]',
  },
  FUCHSIA: {
    id: 'FUCHSIA',
    label: 'Fucsia',
    bg: 'bg-[#86198f]',
    bgSubtle: 'bg-[#701a75]',
    border: 'border-white/10',
    textBase: 'text-white/90',
    textHeading: 'text-white/60',
    textMuted: 'text-white/70',
    hoverBg: 'hover:bg-white/10',
    hoverText: 'hover:text-white',
    activeBg: 'bg-white/15',
    activeText: 'text-white',
    toggleText: 'text-white/70',
    toggleHover: 'hover:bg-white/10',
    pageAccent: 'bg-[#86198f]',
  },
};

export const DEFAULT_SIDEBAR_THEME: SidebarThemeId = 'SLATE';

export const SIDEBAR_THEME_OPTIONS: { id: SidebarThemeId; label: string }[] = [
  { id: 'SLATE', label: SIDEBAR_THEMES.SLATE.label },
  { id: 'SKY', label: SIDEBAR_THEMES.SKY.label },
  { id: 'EMERALD', label: SIDEBAR_THEMES.EMERALD.label },
  { id: 'FUCHSIA', label: SIDEBAR_THEMES.FUCHSIA.label },
];

export function resolveSidebarTheme(themeId: string | null | undefined): SidebarThemeConfig {
  if (themeId && themeId in SIDEBAR_THEMES) {
    return SIDEBAR_THEMES[themeId as SidebarThemeId];
  }
  return SIDEBAR_THEMES[DEFAULT_SIDEBAR_THEME];
}
